import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/AppLayout';
import { S, Icon, PageHeader, BtnPrimary, BtnSecondary, TH, TD, EmptyState, formatDate } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// const FALLBACK = [
//   { _id: '1', requestId: 'DSAR-2025-001', subject: { name: 'Hans Müller', email: 'h.mueller@example.ch' }, requestType: 'access',       createdAt: '2025-01-15', deadline: '2025-02-14', status: 'completed',   assignedTo: { name: 'Sarah K.' } },
//   { _id: '2', requestId: 'DSAR-2025-002', subject: { name: 'Marie Dupont', email: 'm.dupont@example.ch' }, requestType: 'erasure',      createdAt: '2025-01-22', deadline: '2025-02-21', status: 'in_progress', assignedTo: { name: 'Thomas B.' } },
//   { _id: '3', requestId: 'DSAR-2025-003', subject: { name: 'Luca Rossi', email: 'l.rossi@example.ch' }, requestType: 'portability',    createdAt: '2025-02-01', deadline: '2025-03-03', status: 'pending',     assignedTo: null },
//   { _id: '4', requestId: 'DSAR-2025-004', subject: { name: 'Anna Keller', email: 'a.keller@example.ch' }, requestType: 'rectification', createdAt: '2025-02-08', deadline: '2025-03-10', status: 'pending',     assignedTo: null },
//   { _id: '5', requestId: 'DSAR-2025-005', subject: { name: 'Peter Schmid', email: 'p.schmid@example.ch' }, requestType: 'access',      createdAt: '2025-02-14', deadline: '2025-03-16', status: 'in_progress', assignedTo: { name: 'Sarah K.' } },
// ];

const STATUS_MAP_STYLE = {
  completed:   { color: '#10b981', bg: '#ecfdf5', labelKey: 'screens.dsar.statusCompleted' },
  in_progress: { color: '#3b82f6', bg: '#eff6ff', labelKey: 'screens.dsar.statusInProgress' },
  'in-progress': { color: '#3b82f6', bg: '#eff6ff', labelKey: 'screens.dsar.statusInProgress' },
  pending:     { color: '#64748b', bg: '#f1f5f9', labelKey: 'screens.dsar.statusPending' },
  overdue:     { color: '#ef4444', bg: '#fef2f2', labelKey: 'screens.dsar.statusOverdue' },
};

const TYPE_LABEL_KEYS = { access: 'screens.dsar.typeAccess', erasure: 'screens.dsar.typeErasure', portability: 'screens.dsar.typePortability', rectification: 'screens.dsar.typeRectification', objection: 'screens.dsar.typeObjection' };
const TYPE_COLORS = { access: '#3b82f6', erasure: '#ef4444', portability: '#8b5cf6', rectification: '#f59e0b', objection: '#f97316' };

const inputStyle = { width: '100%', padding: '10px 12px', border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 13, outline: 'none', color: S.textPrimary, boxSizing: 'border-box', fontFamily: 'inherit' };

const DSARScreen = () => {
  const { t } = useTranslation();
  const STATUS_MAP = Object.fromEntries(Object.entries(STATUS_MAP_STYLE).map(([k, v]) => [k, { ...v, label: t(v.labelKey) }]));
  const TYPE_LABELS = Object.fromEntries(Object.entries(TYPE_LABEL_KEYS).map(([k, v]) => [k, t(v)]));
  const [requests, setRequests] = useState([]);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [showNewForm, setShowNewForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', email: '', requestType: 'access', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  const types = ['All', 'access', 'erasure', 'portability', 'rectification', 'objection'];
  const statuses = ['All', 'pending', 'in_progress', 'completed', 'overdue'];

  const fetchRequests = useCallback(async () => {
    try {
      const resp = await axios.get(`${BASE_URL}/dsar`, { withCredentials: true });
      if (resp.data?.data?.length >= 0) setRequests(resp.data.data);
    } catch { /* empty state on error */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const filtered = requests.filter(r => {
    const q = search.toLowerCase();
    const name = r.subject?.name || '';
    const email = r.subject?.email || '';
    const id = r.requestId || r._id || '';
    const matchSearch = !q || name.toLowerCase().includes(q) || email.toLowerCase().includes(q) || id.toLowerCase().includes(q);
    const matchType = filterType === 'All' || r.requestType === filterType;
    const matchStatus = filterStatus === 'All' || r.status === filterStatus || r.status === filterStatus.replace('_', '-');
    return matchSearch && matchType && matchStatus;
  });

  const getDaysRemaining = (deadline) => {
    if (!deadline) return null;
    const due = new Date(deadline);
    const now = new Date();
    return Math.round((due - now) / (1000 * 60 * 60 * 24));
  };

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.requestType) return;
    setSubmitting(true);
    try {
      const resp = await axios.post(`${BASE_URL}/dsar`, {
        subject: { name: form.name, email: form.email },
        requestType: form.requestType,
        notes: form.notes,
      }, { withCredentials: true });
      if (resp.data?.data) setRequests(prev => [resp.data.data, ...prev]);
    } catch {
      // Optimistic add with temp ID
      setRequests(prev => [{
        _id: Date.now().toString(), requestId: `DSAR-${new Date().getFullYear()}-${String(prev.length + 1).padStart(3, '0')}`,
        subject: { name: form.name, email: form.email }, requestType: form.requestType,
        createdAt: new Date().toISOString(), status: 'pending', assignedTo: null,
      }, ...prev]);
    } finally {
      setSubmitting(false);
      setShowNewForm(false);
      setForm({ name: '', email: '', requestType: 'access', notes: '' });
    }
  };

  const kpiCounts = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    inProgress: requests.filter(r => r.status === 'in_progress' || r.status === 'in-progress').length,
    overdue: requests.filter(r => r.status === 'overdue').length,
  };

  return (
    <AppLayout>
      <div style={{ flex: 1, overflow: 'auto', padding: '32px 40px' }}>
        <PageHeader
          title={t('screens.dsar.title')}
          subtitle={t('screens.dsar.subtitle')}
          actions={
            <div style={{ display: 'flex', gap: 10 }}>
              <BtnSecondary><Icon name="file-text" size={15} />{t('screens.dsar.export')}</BtnSecondary>
              <BtnPrimary onClick={() => setShowNewForm(true)}><Icon name="plus" size={15} />{t('screens.dsar.newRequest')}</BtnPrimary>
            </div>
          }
        />

        {/* KPI Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
          {[
            { label: t('screens.dsar.totalRequests'), value: kpiCounts.total,      color: '#3b82f6', icon: 'users' },
            { label: t('screens.dsar.pending'),        value: kpiCounts.pending,     color: '#f59e0b', icon: 'bell' },
            { label: t('screens.dsar.inProgress'),    value: kpiCounts.inProgress,  color: '#3b82f6', icon: 'settings' },
            { label: t('screens.dsar.overdue'),        value: kpiCounts.overdue,     color: '#ef4444', icon: 'shield' },
          ].map((card, i) => (
            <div key={i} style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 10, padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 42, height: 42, borderRadius: 9, background: `${card.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name={card.icon} size={18} color={card.color} />
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 700, color: S.textPrimary }}>{card.value}</div>
                <div style={{ fontSize: 12, color: S.textMuted }}>{card.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 10, padding: '14px 20px', marginBottom: 20, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: S.textMuted }}>
              <Icon name="search" size={15} />
            </span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('screens.dsar.searchPh')}
              style={{ width: '100%', padding: '8px 12px 8px 34px', border: `1px solid ${S.borderLight}`, borderRadius: 7, fontSize: 13, outline: 'none', color: S.textPrimary }} />
          </div>
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            style={{ padding: '8px 12px', border: `1px solid ${S.borderLight}`, borderRadius: 7, fontSize: 13, color: S.textPrimary, outline: 'none', cursor: 'pointer' }}>
            {types.map(ty => <option key={ty} value={ty}>{ty === 'All' ? t('screens.dsar.allTypes') : TYPE_LABELS[ty] || ty}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            style={{ padding: '8px 12px', border: `1px solid ${S.borderLight}`, borderRadius: 7, fontSize: 13, color: S.textPrimary, outline: 'none', cursor: 'pointer' }}>
            {statuses.map(s => <option key={s} value={s}>{s === 'All' ? t('screens.dsar.allStatuses') : STATUS_MAP[s]?.label || s}</option>)}
          </select>
        </div>

        {/* Table */}
        <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: S.bgCard }}>
                <TH>{t('screens.dsar.colRequestId')}</TH><TH>{t('screens.dsar.colDataSubject')}</TH><TH>{t('screens.dsar.colRequestType')}</TH><TH>{t('screens.dsar.colReceived')}</TH><TH>{t('screens.dsar.colDueDate')}</TH><TH>{t('screens.dsar.colDaysRemaining')}</TH><TH>{t('screens.dsar.colAssignee')}</TH><TH>{t('screens.dsar.colStatus')}</TH><TH></TH>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} style={{ padding: 0, border: 'none' }}>
                  <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
                  <div style={{display:'flex',justifyContent:'center',alignItems:'center',padding:60}}><div style={{width:36,height:36,border:'3px solid #e2e8f0',borderTopColor:'#7c3aed',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/></div>
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9}><EmptyState icon="users" title={t('screens.dsar.emptyTitle')} subtitle={t('screens.dsar.emptySubtitle')} /></td></tr>
              ) : filtered.map((r, i) => {
                const s = STATUS_MAP[r.status] || STATUS_MAP.pending;
                const days = getDaysRemaining(r.deadline || r.dueDate);
                const typeColor = TYPE_COLORS[r.requestType] || '#3b82f6';
                const typeLabel = TYPE_LABELS[r.requestType] || r.requestType;
                return (
                  <tr key={r._id} style={{ borderTop: `1px solid ${S.borderLight}`, background: i % 2 === 0 ? '#fff' : S.bgCard }}
                    onMouseEnter={e => e.currentTarget.style.background = S.bgHover}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : S.bgCard}>
                    <TD><span style={{ fontFamily: 'monospace', fontSize: 12, color: S.textMuted }}>{r.requestId || r._id}</span></TD>
                    <TD>
                      <div style={{ fontWeight: 500, color: S.textPrimary }}>{r.subject?.name}</div>
                      <div style={{ fontSize: 11, color: S.textMuted }}>{r.subject?.email}</div>
                    </TD>
                    <TD><span style={{ padding: '3px 9px', background: `${typeColor}15`, color: typeColor, borderRadius: 4, fontSize: 12, fontWeight: 500 }}>{typeLabel}</span></TD>
                    <TD>{formatDate(r.createdAt)}</TD>
                    <TD>{r.deadline || r.dueDate ? formatDate(r.deadline || r.dueDate) : '—'}</TD>
                    <TD>
                      {r.status === 'completed' ? (
                        <span style={{ color: '#10b981', fontSize: 12 }}>{t('screens.dsar.done')}</span>
                      ) : days !== null ? (
                        <span style={{ fontSize: 13, fontWeight: 600, color: days < 0 ? '#ef4444' : days <= 5 ? '#f59e0b' : S.textPrimary }}>
                          {days < 0 ? t('screens.dsar.daysOverdue', { n: Math.abs(days) }) : t('screens.dsar.daysRemaining', { n: days })}
                        </span>
                      ) : '—'}
                    </TD>
                    <TD>{r.assignedTo?.name || <span style={{ color: S.textMuted, fontStyle: 'italic' }}>{t('screens.dsar.unassigned')}</span>}</TD>
                    <TD>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', background: s.bg, color: s.color, borderRadius: 20, fontSize: 12, fontWeight: 500 }}>
                        {s.label}
                      </span>
                    </TD>
                    <TD>
                      <button style={{ padding: '5px 12px', border: `1px solid ${S.borderLight}`, borderRadius: 6, background: '#fff', fontSize: 12, cursor: 'pointer', color: S.textSecondary }}>
                        {t('screens.dsar.manage')}
                      </button>
                    </TD>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* New Request Modal */}
        {showNewForm && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: 14, padding: 32, width: 480, boxShadow: '0 25px 60px rgba(0,0,0,0.2)' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary, marginBottom: 20 }}>{t('screens.dsar.newDsarTitle')}</div>
              {[
                { label: t('screens.dsar.dataSubjectName'), field: 'name',    placeholder: 'Hans Müller',          type: 'text' },
                { label: t('screens.dsar.emailAddress'),     field: 'email',   placeholder: 'h.mueller@example.ch', type: 'email' },
              ].map(f => (
                <div key={f.field} style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 13, fontWeight: 500, color: S.textSecondary, display: 'block', marginBottom: 6 }}>{f.label}</label>
                  <input type={f.type} value={form[f.field]} onChange={e => setForm(p => ({ ...p, [f.field]: e.target.value }))} placeholder={f.placeholder} style={inputStyle} />
                </div>
              ))}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, fontWeight: 500, color: S.textSecondary, display: 'block', marginBottom: 6 }}>{t('screens.dsar.requestType')}</label>
                <select value={form.requestType} onChange={e => setForm(p => ({ ...p, requestType: e.target.value }))} style={inputStyle}>
                  <option value="access">{t('screens.dsar.typeAccess')}</option>
                  <option value="erasure">{t('screens.dsar.typeErasure')}</option>
                  <option value="portability">{t('screens.dsar.typePortability')}</option>
                  <option value="rectification">{t('screens.dsar.typeRectification')}</option>
                  <option value="objection">{t('screens.dsar.typeObjection')}</option>
                </select>
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontSize: 13, fontWeight: 500, color: S.textSecondary, display: 'block', marginBottom: 6 }}>{t('screens.dsar.notes')}</label>
                <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder={t('screens.dsar.notesPh')} style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowNewForm(false)} style={{ padding: '10px 20px', background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 13, fontWeight: 600, color: S.textSecondary, cursor: 'pointer' }}>{t('screens.dsar.cancel')}</button>
                <button onClick={handleCreate} disabled={submitting || !form.name || !form.email}
                  style={{ padding: '10px 20px', background: `linear-gradient(135deg, ${S.burgundy}, #722f37)`, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: submitting ? 0.7 : 1 }}>
                  {submitting ? t('screens.dsar.creating') : t('screens.dsar.createRequest')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default DSARScreen;
